/**
 * designations.champions_roll — list of workspace champions, optionally
 * filtered by issue. Compact card surfacing who's with you, with a link
 * to the full /designations page.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "../../components/ui";

export function ChampionsRoll({ spec, workspaceId }: WidgetProps) {
  const issueId = spec.params?.issue_id as string | undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "champions_roll", workspaceId, issueId],
    queryFn: async () => {
      const url =
        `/api/v1/workspaces/${workspaceId}/champions` +
        (issueId ? `?issue_id=${encodeURIComponent(issueId)}` : "");
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      return await res.json();
    },
  });

  if (error) return <DataUnavailable message="Champions could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  if (!data || data.unique_count === 0) {
    return <div className="text-stone-500 text-xs">No champions designated yet.</div>;
  }

  const champs = data.unique_champions as Array<{
    bioguide_id: string;
    display_name: string;
    party: string;
    state: string;
  }>;

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-1.5">
        <div className="text-stone-700 font-semibold">{data.unique_count} champion{data.unique_count === 1 ? "" : "s"}</div>
        <Link
          className="inline-flex items-center gap-1 font-medium text-action hover:underline text-[11px]"
          to="/designations"
        >
          View all <ArrowRight size={11} aria-hidden="true" />
        </Link>
      </div>
      <ul className="space-y-1">
        {champs.slice(0, 8).map((c) => {
          const partyClass =
            c.party?.[0] === "R"
              ? "text-red-700"
              : c.party?.[0] === "D"
              ? "text-sky-800"
              : "text-amber-700";
          return (
            <li key={c.bioguide_id} className="flex items-center gap-2">
              <span className={"font-bold " + partyClass}>{(c.party || "?")[0]}</span>
              <span className="text-stone-700 flex-1 truncate">{c.display_name}</span>
              <span className="font-mono text-stone-500 text-[10px]">{c.state}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
